import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  PackageCheck,
  ListPlus,
  Send,
  CircleCheck,
  PackageCheck as ModalIcon,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  fetchPartiallyDispatched,
  moveOrderToCompleted,
} from "../../../store/slices/orderSlice";
import Button, { ButtonType } from "../../../components/Assets/Button";
import PaginationControls from "../../../utils/PaginationControls";
import ChallanPreviewModal from "./ChallanPreviewModal";
import toast from "react-hot-toast";
import ConfirmationModal from "../../../components/Assets/ConfirmationModal";
import { IOrder, IOrderGoods } from "../../../models/Order.model";

interface Props {
  onAssignClick?: (order: any) => void;
  onDispatchClick?: (orderId: string) => void;
  searchTerm?: string;
  dateRange?: { from: string; to: string };
}

const PartiallyDispatchedTab: React.FC<Props> = ({
  onAssignClick,
  onDispatchClick,
  searchTerm,
  dateRange = { from: "", to: "" },
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { partiallyDispatched, paginations, loading } = useSelector(
    (state: RootState) => state.order,
  );
  const role = useSelector((state: RootState) => state.auth.role);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [isConfirmationModalDispatchOpen, setIsConfirmationModalDispatchOpen] =
    useState(false);
  const [page, setPage] = useState(1);
  const [visibleProductsMap, setVisibleProductsMap] = useState<
    Record<string, number>
  >({});

  // Print Modal State
  const [selectedOrderForPrint, setSelectedOrderForPrint] = useState<
    any | null
  >(null);

  // --- NEW: Dispatched Goods Modal State ---
  const [dispatchedModalOrder, setDispatchedModalOrder] =
    useState<IOrder | null>(null);

  const fetchPartialDispatchCallback = useCallback(async () => {
    dispatch(
      fetchPartiallyDispatched({
        page,
        search: searchTerm,
        startDate: dateRange.from,
        endDate: dateRange.to,
      }),
    );
  }, [dispatch, page, searchTerm, dateRange]);

  useEffect(() => {
    fetchPartialDispatchCallback();
  }, [fetchPartialDispatchCallback]);

  const pagination = paginations.partiallyDispatched;

  const handleOrderAction = (order: any) => {
    if (order.dispatch === "partiallyDispatched") {
      onAssignClick?.(order);
    } else {
      onDispatchClick?.(order._id);
    }
  };

  return (
    <section>
      <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center">
        <span className="w-1 h-6 bg-blue-500 mr-3 rounded-full" />
        Partially Dispatched
        <span className="ml-2 bg-blue-100 text-blue-800 text-sm px-2.5 py-0.5 rounded-full">
          {pagination?.total || 0}
        </span>
      </h2>

      {partiallyDispatched.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No partially dispatched orders
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
          {partiallyDispatched.map((order: IOrder) => {
            const isAssignmentMode = order.dispatch === "partiallyDispatched";
            const limit = visibleProductsMap[order._id] || 5;
            const items = order.goods || [];
            const dispatchedItemsCount = order.dispatchedGoods?.length || 0;

            return (
              <motion.div
                key={order._id}
                layout
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="bg-white rounded-2xl p-4 sm:p-5 lg:p-6 shadow-xs border border-gray-100 hover:shadow-md transition-shadow cursor-pointer relative overflow-hidden flex flex-col min-h-[420px]"
                whileHover={{ y: -4 }}
                onClick={() => handleOrderAction(order)}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 mb-4">
                  <div className="bg-blue-50 p-2 rounded-lg shrink-0">
                    <PackageCheck size={18} className="text-blue-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-slate-900 break-all">
                      Order #{order.orderNumber}
                    </h3>

                    <p className="text-xs text-gray-500 truncate">
                      {order.customerId?.name ||
                        order.whatsappCustomerId?.customerName ||
                        order.whatsappCustomerId?.productRegistration?.name ||
                        "Unknown Customer"}
                    </p>

                    <div
                      className={`mt-2 inline-flex px-2 py-1 rounded text-[10px] font-bold ${
                        order.dispatch === "partial"
                          ? "bg-orange-100 text-orange-600"
                          : "bg-blue-100 text-blue-600"
                      }`}
                    >
                      {order.dispatch === "partial"
                        ? "Partial"
                        : "Partially Dispatched"}
                    </div>
                  </div>

                  {role === "admin" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(order);
                        setIsConfirmationModalDispatchOpen(true);
                      }}
                      className="self-start hover:opacity-80 transition-opacity"
                    >
                      <CircleCheck size={18} className="text-green-700" />
                    </button>
                  )}
                </div>

                {/* --- Goods List --- */}
                <div className="mb-4">
                  <motion.div layout className="space-y-1.5 overflow-hidden">
                    {items.slice(0, 5).map((item: any, idx: number) => (
                      <div
                        key={`${order._id}-${idx}`}
                        className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                      >
                        <span className="font-medium text-gray-800 truncate pr-2">
                          {item.product?.productName ||
                            item.rawMaterial?.name ||
                            "Unknown"}
                        </span>
                        <span className="text-blue-600 font-bold shrink-0">
                          {item.dispatchedQty || 0} / {item.quantity}{" "}
                          {item.unit || ""}
                        </span>
                      </div>
                    ))}

                    <AnimatePresence initial={false}>
                      {limit > 5 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{
                            duration: 0.3,
                            ease: "easeInOut",
                          }}
                          className="space-y-1.5 overflow-hidden mt-1.5"
                        >
                          {items
                            .slice(5, limit)
                            .map((item: any, idx: number) => (
                              <div
                                key={`${order._id}-${idx + 5}`}
                                className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                              >
                                <span className="font-medium text-gray-800 truncate pr-2">
                                  {item.product?.productName ||
                                    item.rawMaterial?.name ||
                                    "Unknown"}
                                </span>
                                <span className="text-blue-600 font-bold shrink-0">
                                  {item.dispatchedQty || 0} / {item.quantity}{" "}
                                  {item.unit || ""}
                                </span>
                              </div>
                            ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {items.length > limit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisibleProductsMap((prev) => ({
                          ...prev,
                          [order._id]: limit + 5,
                        }));
                      }}
                      className="w-full py-1.5 text-[10px] text-blue-700 font-bold bg-blue-50 hover:bg-blue-100 rounded-md transition-colors mt-2"
                    >
                      Show {Math.min(5, items.length - limit)} more
                    </button>
                  )}
                </div>

                {/* --- NEW: Trigger Dispatched Goods Modal Button --- */}
                {dispatchedItemsCount > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDispatchedModalOrder(order);
                    }}
                    className="mb-3 w-full flex items-center justify-between px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg text-[11px] font-semibold hover:bg-green-100 transition-colors"
                  >
                    <span className="flex items-center gap-1.5">
                      <ModalIcon size={14} /> Dispatched Goods
                    </span>
                    <span className="bg-green-600 text-white text-[10px] px-1.5 py-0.2 rounded-full">
                      {dispatchedItemsCount}
                    </span>
                  </button>
                )}

                <div className="mt-auto space-y-2">
                  <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedOrderForPrint(order)}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-slate-400 text-white rounded-xl text-[10px] font-bold shadow-xs transition-colors hover:bg-slate-500"
                    >
                      <FileText size={12} />
                      Print Challan
                    </button>
                  </div>

                  <Button
                    type={ButtonType.Submit}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOrderAction(order);
                    }}
                    className="w-full text-xs flex items-center justify-center gap-1.5 py-2.5"
                  >
                    {isAssignmentMode ? (
                      <>
                        <ListPlus size={14} /> Assign Remaining
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Continue Dispatch
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {pagination && pagination.total > 0 && (
        <div className="mt-8 border-t border-gray-100 pt-6">
          <PaginationControls
            currentPage={page}
            totalPages={pagination.pages}
            onPageChange={setPage}
            isLoading={loading}
            totalItems={pagination.total}
          />
        </div>
      )}

      {/* --- NEW: Dispatched Goods Modal (z-index 110) --- */}
      <AnimatePresence>
        {dispatchedModalOrder && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDispatchedModalOrder(null)}
              className="absolute inset-0 bg-black/50 backdrop-blur-xs"
            />

            {/* Modal Dialog */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden z-10 border border-slate-100 flex flex-col max-h-[80vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 bg-slate-50">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-green-100 text-green-700 rounded-lg">
                    <ModalIcon size={18} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-base">
                      Dispatched Goods
                    </h3>
                    <p className="text-xs text-slate-500">
                      Order #{dispatchedModalOrder.orderNumber}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDispatchedModalOrder(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-5 overflow-y-auto space-y-3">
                {dispatchedModalOrder.dispatchedGoods &&
                dispatchedModalOrder.dispatchedGoods.length > 0 ? (
                  dispatchedModalOrder.dispatchedGoods.map(
                    (item: IOrderGoods, idx: number) => {
                      const name =
                        item.product?.productName ||
                        item.rawMaterial?.name ||
                        "Unknown Material/Product";
                      const qty = (item as any).dispatchedQty ?? item.quantity;

                      return (
                        <div
                          key={item._id || idx}
                          className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl"
                        >
                          <div className="min-w-0 pr-2">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {name}
                            </p>
                            <span className="inline-block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-0.5">
                              {item.type || "N/A"}
                            </span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-xs font-bold text-green-600 bg-green-50 border border-green-200 px-2.5 py-1 rounded-md inline-block">
                              {qty} {item.unit || "unit"}
                            </span>
                          </div>
                        </div>
                      );
                    },
                  )
                ) : (
                  <p className="text-center text-sm text-slate-500 py-6">
                    No dispatched goods found for this order.
                  </p>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-100 bg-slate-50 flex justify-end">
                <button
                  onClick={() => setDispatchedModalOrder(null)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold text-xs rounded-xl transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ChallanPreviewModal
        order={selectedOrderForPrint}
        isOpen={!!selectedOrderForPrint}
        onClose={() => setSelectedOrderForPrint(null)}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalDispatchOpen}
        onClose={() => {
          setIsConfirmationModalDispatchOpen(false);
          setSelectedOrder(null);
        }}
        onConfirm={async () => {
          const selectedOrderId = selectedOrder?._id;
          if (selectedOrderId) {
            const res = await dispatch(moveOrderToCompleted(selectedOrderId));
            if (res.payload?.success) {
              setIsConfirmationModalDispatchOpen(false);
              setSelectedOrder(null);
            }
            await fetchPartialDispatchCallback();
          } else {
            toast.error("OrderId not selected");
          }
        }}
        title={`#${selectedOrder?.orderNumber}`}
        subTitle={`Move Order for ${
          selectedOrder?.customerId?.name ||
          selectedOrder?.whatsappCustomerId?.productRegistration?.name ||
          selectedOrder?.whatsappCustomerId?.customerName ||
          "Unknown Customer"
        } to completed dispatch?`}
        description="This will move the order to completed dispatch and remove it from pending dispatch without updating stock."
        confirmText="Yes, Move to Completed"
        cancelText="Cancel"
        loading={loading}
      />
    </section>
  );
};

export default PartiallyDispatchedTab;
